/** Pure helpers for terminals: what a pane shows, and which terminals the split view shows. */
import { TERM_LIMITS } from '@orgus/protocol';
import type { Session, TermSummary } from '@orgus/protocol';
import type { RowStatusProps, TerminalPaneState } from '@orgus/ui';

export type SplitLayout = 1 | 2 | 4;

export const LAYOUT_OPTIONS: { value: string; label: string }[] = [
  { value: '1', label: 'Single' },
  { value: '2', label: 'Side by side' },
  { value: '4', label: 'Grid' },
];

/** The pane state for a terminal, given the socket's own state. */
export function paneState(term: Pick<TermSummary, 'state'> | null, socket: 'connecting' | 'open' | 'closed' | 'error'): TerminalPaneState {
  if (socket === 'error') return 'error';
  if (term?.state === 'exited') return 'ended';
  if (term?.state === 'disconnected') return 'disconnected';
  if (socket === 'connecting' || !term) return 'connecting';
  if (socket === 'closed') return 'error';
  return 'live';
}

export function agentLabel(t: Pick<TermSummary, 'kind'>): string {
  return t.kind === 'session' ? 'Claude Code' : t.kind === 'sign-in' ? 'Claude Code sign-in' : 'Shell';
}

/** The plain status of a terminal in the picker: live is at work, the rest are grey. */
export function termRowStatus(t: Pick<TermSummary, 'state' | 'lock'>): Pick<RowStatusProps, 'tone' | 'icon' | 'label'> {
  switch (t.state) {
    case 'live':
      return { tone: 'working', icon: 'working', label: t.lock ? `Live, ${t.lock.label} is typing` : 'Live' };
    case 'starting':
      return { tone: 'neutral', icon: 'queued', label: 'Starting' };
    case 'disconnected':
      return { tone: 'neutral', icon: 'offline', label: 'Machine offline' };
    default:
      return { tone: 'neutral', icon: 'ended', label: 'Exited' };
  }
}

/**
 * The ?show= list: null when the person has not picked (the page picks for them), else the ids,
 * which may be none at all when they hid every terminal.
 */
export function parseShown(param: string | null): string[] | null {
  return param === null ? null : param.split(',').filter(Boolean);
}

/**
 * Terminals the split view shows. With no choice made, the newest live ones (the page then pins
 * them, so one that ends stays on screen with its last output). With a choice, the ones asked for,
 * ended or not; if none of them exist any more, the newest live ones again. An empty choice shows
 * none.
 */
export function visibleTerms(all: TermSummary[], requested: string[] | null, max: number = TERM_LIMITS.maxSplit): TermSummary[] {
  if (requested && !requested.length) return [];
  const byId = new Map(all.map((t) => [t.id, t]));
  const asked = (requested ?? []).map((id) => byId.get(id)).filter((t): t is TermSummary => !!t);
  if (asked.length) return asked.slice(0, max);
  return [...all]
    .filter((t) => t.state !== 'exited')
    .sort((a, b) => b.startedAt - a.startedAt)
    .slice(0, max);
}

/** Toggle a terminal in or out of the split view, keeping at most `max` (the oldest choice drops). */
export function toggleShown(shown: string[], id: string, max: number = TERM_LIMITS.maxSplit): string[] {
  if (shown.includes(id)) return shown.filter((x) => x !== id);
  return showOne(shown, id, max);
}

/** Add a terminal to the split view (a no-op when it is already there), keeping at most `max`. */
export function showOne(shown: string[], id: string, max: number = TERM_LIMITS.maxSplit): string[] {
  if (shown.includes(id)) return shown;
  const next = [...shown, id];
  return next.length > max ? next.slice(next.length - max) : next;
}

/**
 * The ids to write into ?show= so what the page picked stays put, or null when nothing needs
 * pinning (the person already chose, or nothing is on screen).
 */
export function pinShown(requested: string[] | null, shown: Pick<TermSummary, 'id'>[]): string[] | null {
  if (!shown.length) return null;
  const ids = shown.map((t) => t.id);
  if (requested && requested.length === ids.length && requested.every((id, i) => id === ids[i])) return null;
  return requested === null || requested.length ? ids : null;
}

/** The layout that fits this many panes, unless the person picked a bigger one. */
export function layoutFor(count: number, picked: SplitLayout | null): SplitLayout {
  if (picked) return picked;
  return count <= 1 ? 1 : count === 2 ? 2 : 4;
}

/** The terminal a session runs in (a live one first, else the latest that ended). */
export function termForSession(all: TermSummary[], sessionId: string): TermSummary | undefined {
  const mine = all.filter((t) => t.sessionId === sessionId).sort((a, b) => b.startedAt - a.startedAt);
  return mine.find((t) => t.state !== 'exited') ?? mine[0];
}

/** Why a session has no terminal to take over, in words for the drawer. */
export function noTerminalReason(s: Pick<Session, 'origin'>): 'watched' | 'not-in-pty' {
  return s.origin === 'orgus' ? 'not-in-pty' : 'watched';
}

/** "owner is typing", "You have control", or null. */
export function lockFor(t: Pick<TermSummary, 'lock'> | null, you: string | null): { label: string; you: boolean } | null {
  if (!t?.lock) return null;
  return { label: t.lock.label, you: !!you && t.lock.actor === you };
}
